import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KnowledgeExtractionConfig } from '@/types/knowledge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SchedulePicker } from './SchedulePicker';

const configSchema = z.object({
  promptTemplate: z.string().optional(),
  model: z.string().optional(),
  temperature: z.number().min(0).max(1).optional(),
  cronSchedule: z.string().optional(),
  enabled: z.boolean().optional(),
});

type ConfigFormData = z.infer<typeof configSchema>;

interface ConfigFormProps {
  config: KnowledgeExtractionConfig;
  onSubmit: (data: ConfigFormData) => void;
  isLoading?: boolean;
}

export function ConfigForm({ config, onSubmit, isLoading }: ConfigFormProps) {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
    defaultValues: {
      promptTemplate: config.promptTemplate,
      model: config.model,
      temperature: config.temperature,
      cronSchedule: config.cronSchedule,
      enabled: config.enabled,
    },
  });

  const enabled = watch('enabled');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center justify-between">
        <Label htmlFor="enabled">Extração Automática</Label>
        <Switch
          id="enabled"
          checked={enabled}
          onCheckedChange={(checked) => setValue('enabled', checked)}
        />
      </div>

      <div>
        <Label htmlFor="promptTemplate">Prompt Template</Label>
        <Textarea
          id="promptTemplate"
          rows={10}
          {...register('promptTemplate')}
        />
        {errors.promptTemplate && (
          <p className="text-sm text-red-500 mt-1">{errors.promptTemplate.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="model">Modelo OpenAI</Label>
        <Input id="model" {...register('model')} placeholder="gpt-4o-mini" />
        <div className="flex flex-wrap gap-2 mt-2">
          {['gpt-4.1', 'gpt-5', 'gpt-5-mini', 'gpt-5.2'].map((modelName) => (
            <Button
              key={modelName}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setValue('model', modelName)}
            >
              {modelName}
            </Button>
          ))}
        </div>
        {errors.model && (
          <p className="text-sm text-red-500 mt-1">{errors.model.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="temperature">Temperature (0-1)</Label>
        <Input
          id="temperature"
          type="number"
          step="0.1"
          {...register('temperature', { valueAsNumber: true })}
        />
        {errors.temperature && (
          <p className="text-sm text-red-500 mt-1">{errors.temperature.message}</p>
        )}
      </div>

      <div>
        <Label>Agendamento da Extração</Label>
        <div className="mt-2 p-4 border rounded-lg bg-card">
          <SchedulePicker
            value={watch('cronSchedule') || '0 20 * * *'}
            onChange={(cron) => setValue('cronSchedule', cron)}
          />
        </div>
        {errors.cronSchedule && (
          <p className="text-sm text-red-500 mt-1">{errors.cronSchedule.message}</p>
        )}
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Salvando...' : 'Salvar Configuração'}
      </Button>
    </form>
  );
}
